import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import type {
  PublicationAssessmentsType,
  PublicationAssessmentsValue,
} from './types';
import { PublicationAssessmentsBox } from './PublicationAssessmentsBox';
import {
  useCreatePublicationAssessment,
  useGetPublicationAssessment,
  type FilterType,
} from '../../../../graphql';
import {
  Loading,
  Alert,
  type AlertDetails,
} from '../../../../../../components';

type PublicationAssessmentsProps = {
  publicationIds: string[];
  refetchAssessment?: () => void;
  onJumpToNext?: () => void;
  isViewMode?: boolean;
};

export const PublicationAssessments: React.FunctionComponent<
  PublicationAssessmentsProps
> = props => {
  const { applicationId = '' } = useParams();
  const {
    publicationIds = [], //  this arr would update instantly based on publication data from the application
    isViewMode = false,
    refetchAssessment = () => {},
    onJumpToNext = () => {},
  } = props;

  let initialValues: PublicationAssessmentsType[] = publicationIds.map(
    publicationReferenceId => ({
      publicationReferenceId,
      publicationType: undefined,
    }),
  );

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  const filter: FilterType = {
    applicationId,
  };
  const {
    publicationAssessments,
    loading,
    error: publicationAssessmentsError,
  } = useGetPublicationAssessment(filter);

  if (!loading && publicationAssessments.length > 0) {
    // merge the history

    const initialValuesWithHistory: PublicationAssessmentsType[] =
      initialValues.map(pub => {
        const { publicationReferenceId } = pub;
        const assessedPub = publicationAssessments.find(
          pub => pub.publicationReferenceId === publicationReferenceId,
        );
        return {
          publicationReferenceId,
          publicationType: assessedPub?.publicationType ?? undefined,
        };
      });

    initialValues = [...initialValuesWithHistory];
  }

  const createPublicationAssessment = useCreatePublicationAssessment();

  const handleSubmit = async (
    value: PublicationAssessmentsValue,
  ): Promise<void> => {
    try {
      const variables = {
        input: {
          applicationId,
          publicationAssessments: value.publicationAssessments,
        },
      };

      await createPublicationAssessment({
        variables,
      });

      setAlertDetails({
        alertContent: 'Saved Publication Assessment Successfully.',
        alertType: 'success',
      });
      refetchAssessment();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      setAlertDetails({
        alertContent:
          `Saved Publication Assessment Failed.` + (error as Error)?.message,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  if (publicationAssessmentsError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            'Error Occurs.' + (publicationAssessmentsError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return (
    <ContentBox>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />

      {loading ? (
        <Loading />
      ) : (
        <PublicationAssessmentsBox
          initialValues={initialValues}
          isViewMode={isViewMode}
          onSubmit={handleSubmit}
          onJumpToNext={onJumpToNext}
        />
      )}
    </ContentBox>
  );
};

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;
